import {
  goBack,
  lightImpactVibrate,
  onReady,
  updateAppStatusBar,
  trackEvent,
} from '../../utils/appBridge'
import BackButton from '../../components/ui-components/BackButton'
import SharedLayoutMenu from '../../components/peer-analyser/SharedLayoutMenu'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { usePeerAnalyserStore } from '../../state-management/peerAnalyserStore'
import { motion, AnimatePresence } from 'framer-motion'
import { EventNames } from '../../types/misc.types'
import { FundNature } from '../../types/peer-analyser'
import { getFundNatureLabel, getFundNatureFromCategory } from '../../utils/peerAnalyserUtils'
import { preloadAllCategoryAssets } from '../../config/categoryConfig'
import RenderHTML from '../../components/ui-components/RenderHTML'
import { getTopPadding } from '../../utils/sessionStorage'

// Global Elite Unlocked Modal Component
function EliteUnlockedModal() {
  const showEliteUnlockedModal = usePeerAnalyserStore((state) => state.showEliteUnlockedModal)
  const tenureInDays = usePeerAnalyserStore((state) => state.tenureInDays)

  return (
    <AnimatePresence mode='wait'>
      {showEliteUnlockedModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className='fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center'
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 10 }}
            transition={{
              type: 'spring',
              damping: 25,
              stiffness: 300,
              duration: 0.5
            }}
            className='bg-white rounded-xl p-8 text-center shadow-lg py-4 px-2'
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1, rotate: [0, 10, -10, 0] }}
              transition={{
                scale: { delay: 0.2, type: 'spring', damping: 15, stiffness: 300 },
                rotate: { delay: 0.3, duration: 0.5 }
              }}
              className='text-2xl font-bold text-green-600 mb-2 flex items-center justify-center'
            >
              <svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none' viewBox='0 0 24 24'>
                <g clipPath='url(#a)'>
                  <path fill='#fff' d='M4 6h16v12H4z' />
                  <motion.path
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ delay: 0.4, duration: 0.4, ease: 'easeInOut' }}
                    fill='#CC5C2D'
                    d='M12 0a12 12 0 1 0 12 12A12.014 12.014 0 0 0 12 0Zm5.86 10.06-6.6 6.6a1.5 1.5 0 0 1-2.12 0l-3-3a1.5 1.5 0 1 1 2.12-2.12l1.94 1.938 5.54-5.539a1.5 1.5 0 1 1 2.12 2.122Z'
                  />
                </g>
                <defs>
                  <clipPath id='a'>
                    <path fill='#fff' d='M0 0h24v24H0z' />
                  </clipPath>
                </defs>
              </svg>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.3 }}
              className='text-[13px] text-gray-800'
            >
              Unlocked for {tenureInDays} {tenureInDays > 1 ? 'days' : 'day'}
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default function PeerAnalyserPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const source = searchParams?.get('source') ?? 'NONE'
  const onReadyLock = useRef<boolean>(false)
  const [isContentLoaded, setIsContentLoaded] = useState(false)
  const fundNature = usePeerAnalyserStore((state) => state.fundNature)
  const setFundNature = usePeerAnalyserStore((state) => state.setFundNature)
  const setSelectedCategoryId = usePeerAnalyserStore((state) => state.setSelectedCategoryId)
  const setShowFilterBottomSheet = usePeerAnalyserStore((state) => state.setShowFilterBottomSheet)
  const showFilterBottomSheet = usePeerAnalyserStore((state) => state.showFilterBottomSheet)
  preloadAllCategoryAssets()

  // Read fundCategory or fundNature from query params and set state
  useEffect(() => {
    const fundCategoryParam = searchParams.get('fundCategory')
    const fundNatureParam = searchParams.get('fundNature')

    if (fundCategoryParam) {
      // If fundCategory is provided, determine fund nature from it and set category
      const determinedFundNature = getFundNatureFromCategory(fundCategoryParam)
      setFundNature(determinedFundNature)
      setSelectedCategoryId(fundCategoryParam)
    } else if (fundNatureParam) {
      // If fundNature is provided, validate and set it
      const validFundNature = Object.values(FundNature).includes(fundNatureParam as FundNature)
        ? (fundNatureParam as FundNature)
        : FundNature.EQUITY
      setFundNature(validFundNature)
    }
  }, [searchParams, setFundNature, setSelectedCategoryId])

  useEffect(() => {
    updateAppStatusBar('FFFFFF', 'dark')
    // Track page load
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: 'PeerAnalyser',
        screenType: 'PowerSelect',
        eventId: `${source}`
      }
    })
  }, [])

  // Call onReady when content is loaded
  useEffect(() => {
    if (isContentLoaded && !onReadyLock.current) {
      onReadyLock.current = true
      onReady()
    }
  }, [isContentLoaded])

  const handleBack = () => {
    lightImpactVibrate()

    // Track back button click
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'PeerAnalyser',
        buttonLabel: 'Back'
      }
    })

    goBack()
    // else navigate(-1)
  }

  const handleFilterClick = () => {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'PeerAnalyser',
        buttonLabel: 'Filter',
        eventId: 'filter_dropdown'
      }
    })
    setShowFilterBottomSheet(true)
  }

  // Get current fund nature label for filter dropdown from store
  const fundNatureLabel = getFundNatureLabel(fundNature || FundNature.EQUITY)

  const backButtonRef = useRef<HTMLButtonElement>(null)

  // Move initial focus to Back so TalkBack lands on it first (Power Select_B01)
  useEffect(() => {
    const id = window.setTimeout(() => {
      backButtonRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(id)
  }, [])

  return (
    <div className='h-full w-full flex flex-col'>
      <header
        style={{ height: `${getTopPadding() + 40}px` }}
        key='small'
        id='fullHeaderSmall'
        className='w-full flex z-[99]'
      >
        <div
          style={{ paddingTop: `calc(${getTopPadding()}px + 16px)` }}
          className={`flex items-center relative w-full pb-5 pr-5 pl-[19px] h-full bg-white border-b border-white`}
        >
          <div className='flex z-1 pt-[2px]'>
            <BackButton ref={backButtonRef} size={24} onClick={handleBack} />
          </div>
          <div className='w-full flex items-center pl-5'>
            <h1 className='max-w-[70%] animate-fade-150 truncate font-[500] text-[16px] m-0'>Power Select</h1>
          </div>
          <button
            type='button'
            onClick={handleFilterClick}
            className='flex items-center gap-1 cursor-pointer absolute right-5 min-h-6 py-1 border-none bg-transparent px-1'
            aria-label={`Select Category, ${fundNatureLabel}`}
            aria-haspopup='dialog'
            aria-expanded={showFilterBottomSheet}
          >
            <span className='text-[#6A6E6E] text-[13px] font-[400]' aria-hidden='true'>
              {fundNatureLabel}
            </span>
            <span className='pt-[1px]' aria-hidden='true'>
              <RenderHTML innerHTMLText={DownCaratIcon} />
            </span>
          </button>
        </div>
      </header>
      <SharedLayoutMenu onContentLoaded={() => setIsContentLoaded(true)} />
      <EliteUnlockedModal />
    </div>
  )
}

const DownCaratIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
  <path d="M1.20711 2L5.79289 2C6.23835 2 6.46143 2.53857 6.14645 2.85355L3.85355 5.14645C3.65829 5.34171 3.34171 5.34171 3.14645 5.14645L0.853554 2.85355C0.538571 2.53857 0.761654 2 1.20711 2Z" fill="#6A6E6E"/>
</svg>`
